/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from "react";
import { Modal, Button, Row, Col, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import FormLabel from "../../components/Common/FormLabel";
import FormControl from "../../components/Common/FormControl";
import ToggleButtonWithInfo from "../../components/Common/ToggleButtonWithInfo";
import { isValidDecimalInput } from "../../utils/Utils";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import DatePicker from "react-datepicker";
import {
  updateWeightGoalThunk,
  resetWeightGoalState,
} from "../../redux/Slices/weightGoalSlice";
import Loader from "../../components/Common/Loader";
import ToastNotification from "../../components/Common/ToastNotification";
import { getWeightByUnit, lbsToKg } from "../../utils/unitConversion";
import { UnitContext } from "../../hooks/UnitProvider";
import appInsights from "../../AppInsights";

const EditWeightGoalModal = ({ show, onClose, selectedClientId }) => {
  const { trans } = useCustomTranslation();
  const { unit } = useContext(UnitContext);

  const dispatch = useDispatch();
  const { profileData } = useSelector((state) => state.profile);
  const coachId = profileData?.coachId;

  const [targetWeight, setTargetWeight] = useState(0);
  const [targetDate, setTargetDate] = useState(null);
  const [maintainCurrentWeight, setMaintainCurrentWeight] = useState(false);
  const [currentWeight, setCurrentWeight] = useState(0);
  const [errors, setErrors] = useState({ targetWeight: "", targetDate: null });
  const [toastConfig, setToastConfig] = useState({
    type: "",
    message: "",
    heading: "",
  });
  const [showToast, setShowToast] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const {
    loading,
    success: updateSuccess,
    error: updateError,
  } = useSelector((state) => state.weightGoal);

  const { clientCheckInData } = useSelector((state) => state.clientCheckIn);

  // AppInsights – Page View (Modal Open)
  useEffect(() => {
    if (show) {
      appInsights.trackPageView({
        name: "EditWeightGoalModal",
        properties: {
          clientId: selectedClientId,
          coachId,
        },
      });
    }
  }, [show, selectedClientId, coachId]);

  useEffect(() => {
    if (unit === "imperial") {
      clientCheckInData?.weight &&
        setCurrentWeight(getWeightByUnit(unit, clientCheckInData.weight));
      clientCheckInData?.targetWeight &&
        setTargetWeight(getWeightByUnit(unit, clientCheckInData.targetWeight));
    } else {
      setCurrentWeight(getWeightByUnit(unit, clientCheckInData?.weight || 0));
      setTargetWeight(
        getWeightByUnit(unit, clientCheckInData?.targetWeight || 0),
      );
    }
    // Ensure targetDate is either a valid Date or null
    if (clientCheckInData?.targetWeightdate) {
      const date = new Date(clientCheckInData.targetWeightdate);
      if (!isNaN(date)) {
        setTargetDate(date);
      } else {
        setTargetDate(null);
      }
    } else {
      setTargetDate(null);
    }
  }, [clientCheckInData, unit]);

  const handleConfirm = async () => {
    appInsights.trackEvent({
      name: "ConfirmWeightGoalUpdate",
      properties: {
        clientId: selectedClientId,
        coachId,
        maintainCurrentWeight,
        unit,
      },
    });

    const newErrors = {};

    // Validate Target Weight
    if (
      !targetWeight ||
      !isValidDecimalInput(targetWeight) ||
      targetWeight <= 0
    ) {
      newErrors.targetWeight = trans("Client.validTargetWeight");
    }

    // Validate Target Date
    if (!targetDate) {
      newErrors.targetDate = trans("Client.validTargetDate");
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    const formattedTargetDate = targetDate?.toISOString()?.split("T")[0];

    const payload = {
      coachId,
      clientId: selectedClientId,
      targetWeight,
      targetDate: formattedTargetDate,
      currentWeight,
      maintainCurrentWeight,
    };
    if (unit === "imperial") {
      if (payload.targetWeight || payload.currentWeight) {
        payload.targetWeight = lbsToKg(payload?.targetWeight);
        payload.currentWeight = lbsToKg(payload?.currentWeight);
      }
    }

    dispatch(updateWeightGoalThunk({ payload }));
  };

  const handleDateChange = (rawDate) => {
    setTargetDate(rawDate);
  };

  const onHandleChange = (e) => {
    const { value } = e.target;
    if (isValidDecimalInput(value)) {
      setTargetWeight(value);
    }
  };

  // Toast notification logic
  useEffect(() => {
    if (updateSuccess) {
      appInsights.trackEvent({
        name: updateSuccess
          ? "WeightGoalUpdateSuccess"
          : "WeightGoalUpdateFailed",
        properties: {
          clientId: selectedClientId,
          coachId,
        },
      });

      setToastConfig({
        type: "success",
        message: updateSuccess,
        heading: trans("Common.success"),
      });
      setShowToast(true);
      dispatch(resetWeightGoalState());
      setIsClosing(true);
      setTimeout(() => {
        setIsClosing(false);
        onClose(true);
      }, 3000);
    } else if (updateError) {
      appInsights.trackException({
        exception: new Error(updateError),
        properties: {
          clientId: selectedClientId,
          coachId,
        },
      });

      setToastConfig({
        type: "error",
        message: updateError,
        heading: trans("Common.failed"),
      });
      setShowToast(true);
      dispatch(resetWeightGoalState());
      setIsClosing(true);
      setTimeout(() => {
        setIsClosing(false);
        onClose(true);
      }, 3000);
    }
  }, [updateSuccess, updateError, dispatch, trans]);

  return (
    <>
      <Modal
        show={show}
        onHide={() => {
          appInsights.trackEvent({
            name: "CloseEditWeightGoalModal",
            properties: {
              clientId: selectedClientId,
              coachId,
            },
          });
          onClose(true);
        }}
        centered
        className="edit-weight-goal-modal"
      >
        {loading || isClosing ? (
          <Loader />
        ) : (
          <>
            <Modal.Header closeButton>
              <Modal.Title className="fs-5">Edit Weight Goal</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row className="mb-1">
                <Col>
                  <Form.Group controlId="formTargetWeight">
                    <FormLabel label={"Target Weight"} required />
                    <div className="position-relative">
                      <FormControl
                        type="text"
                        value={targetWeight}
                        placeholder={trans("Client.enterTargetWeight")}
                        name="targetWeight"
                        onChange={onHandleChange}
                        isInvalid={!!errors.targetWeight}
                        feedback={errors.targetWeight}
                        height="40"
                        className="rounded-3"
                        isDisabled={maintainCurrentWeight}
                      />
                      <p className="position-absolute mb-0 lb-text">
                        {unit === "imperial" ? "lbs" : "kg"}
                      </p>
                    </div>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group controlId="formTargetDate ">
                    <FormLabel label="Target Date" required />
                    <div className="d-flex flex-column">
                      <div
                        className="position-relative date-picker"
                        style={{ width: "fit-content" }}
                      >
                        <DatePicker
                          selected={targetDate}
                          onChange={handleDateChange}
                          dateFormat="MMM d, yyyy"
                          placeholderText="Select Date"
                          className={`custom-datepicker w-100 ${
                            errors.targetDate ? "is-invalid" : ""
                          }`}
                          minDate={new Date()}
                        />
                        <i className="bi bi-calendar3 calendar-icon"></i>
                      </div>

                      {errors.targetDate && (
                        <div className="invalid-feedback d-block">
                          {errors.targetDate}
                        </div>
                      )}
                    </div>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="align-items-center mt-3">
                <Col>
                  <FormLabel label="Maintain Current Weight" />
                  <h6 className="fs-6 fw-normal">
                    CURRENT WEIGHT: {currentWeight}{" "}
                    {unit === "imperial" ? "lbs" : "kg"}
                  </h6>
                </Col>
                <Col className="text-end">
                  <ToggleButtonWithInfo
                    id="toggle-maintain-weight"
                    checked={maintainCurrentWeight}
                    onChange={(e) => {
                      appInsights.trackEvent({
                        name: "ToggleMaintainCurrentWeight",
                        properties: {
                          clientId: selectedClientId,
                          coachId,
                          enabled: e.target.checked,
                        },
                      });
                      setTargetWeight(currentWeight);
                      setMaintainCurrentWeight(e.target.checked);
                    }}
                    className="mt-1 justify-content-end align-items-end"
                    fontSize="fs-4"
                  />
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="outline-primary"
                onClick={onClose}
                size="sm"
                className="cancel-button w-25 p-1"
              >
                Cancel
              </Button>

              <Button
                size="sm"
                className="saveButton w-25 ml-2"
                onClick={handleConfirm}
              >
                Confirm
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>

      {showToast && (
        <>
          <ToastNotification
            type={toastConfig.type}
            message={toastConfig.message}
            heading={toastConfig.heading}
            onClose={() => {
              onClose(true);
              setShowToast(false);
            }}
          />
          {updateError && onClose(true)}
        </>
      )}
    </>
  );
};

export default EditWeightGoalModal;
